import { useState, useCallback, useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'
import type { SurveyQuestion, InterviewQuestion } from '@bandos/shared'

export type BatchQuestion = SurveyQuestion | InterviewQuestion

export interface NodeMetadataEntry {
  nodeLabel: string
  nodeCategory: string
}

interface ValidationState<Q extends BatchQuestion> {
  /** Flat ordered list of questions — script order. */
  questions: Q[]
  /** Parallel array of stable client-side IDs for drag-and-drop. */
  questionIds: string[]
}

function initializeState<Q extends BatchQuestion>(questions: Q[]): ValidationState<Q> {
  return {
    questions: questions.slice(),
    questionIds: questions.map(() => uuidv4()),
  }
}

/**
 * State management for the batch question review panel.
 *
 * The server returns a flat list of questions in script order, each carrying
 * `sourceNodeIds` for attribution. The editor preserves that flat order and
 * exposes the metadata map so attribution + coverage panels can render
 * without re-grouping by source node. Users can add, delete, reorder, and
 * (via the per-question popover) re-attribute questions without any
 * AI-imposed sectioning.
 */
export function useBatchQuestionState<Q extends BatchQuestion>(
  initialQuestions: Q[],
  metadataByNode: Record<string, NodeMetadataEntry>
) {
  const [state, setState] = useState<ValidationState<Q>>(() => initializeState(initialQuestions))

  const setQuestions = useCallback((next: Q[]) => {
    setState((prev) => ({ ...prev, questions: next }))
  }, [])

  const setQuestionIds = useCallback((next: string[]) => {
    setState((prev) => ({ ...prev, questionIds: next }))
  }, [])

  /** Replace the question at `index` and keep everything else (incl. dnd ids). */
  const updateQuestion = useCallback((index: number, updated: Q) => {
    setState((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => (i === index ? updated : q)),
    }))
  }, [])

  /** Remove the question and its dnd id at the same index. */
  const removeQuestion = useCallback((index: number) => {
    setState((prev) => ({
      questions: prev.questions.filter((_, i) => i !== index),
      questionIds: prev.questionIds.filter((_, i) => i !== index),
    }))
  }, [])

  /** Append a fresh question with a new dnd id. */
  const addQuestion = useCallback((question: Q) => {
    setState((prev) => ({
      questions: [...prev.questions, question],
      questionIds: [...prev.questionIds, uuidv4()],
    }))
  }, [])

  /** Reorder both arrays in lockstep — used by the drag-and-drop handler. */
  const reorderQuestions = useCallback((newQuestions: Q[], newIds: string[]) => {
    setState({ questions: newQuestions, questionIds: newIds })
  }, [])

  const metadataByNodeMemo = useMemo(() => metadataByNode, [metadataByNode])

  return {
    questions: state.questions,
    questionIds: state.questionIds,
    metadataByNode: metadataByNodeMemo,
    setQuestions,
    setQuestionIds,
    updateQuestion,
    removeQuestion,
    addQuestion,
    reorderQuestions,
  }
}
